import React from "react";
import { formatDateWithSuffix } from "../../utils/formatTime";
import {
  maskFailedForType,
  toDisplayStatus,
} from "../../utils/activity/displayStatus";
import type { ActivityItem } from "../../utils/activity/normalizeTransaction";
import ActivityItemIcon from "./ActivityItemIcon";

const STATUS_COLOR: Record<string, string> = {
  pending: "text-[#CB9200]",
  "in progress": "text-[#60B7F6]",
  completed: "text-[#03C668]",
  success: "text-[#03C668]",
  failed: "text-red-700",
};

/**
 * One transaction row. Renders a pre-normalized ActivityItem, so there are no
 * per-transactionType ternaries here — this single component replaces the row
 * markup that used to be duplicated four times across the app.
 */
const ActivityRow = ({
  item,
  giftCardImage,
  hideAmounts,
  onSelect,
}: {
  item: ActivityItem;
  giftCardImage?: string;
  hideAmounts?: boolean;
  onSelect: (raw: any) => void;
}) => {
  // NB: the PWA's masking rule, not mobile's `type === "payout"`. Using mobile's
  // here would make a failed withdrawal read "Failed" in this row and "PENDING"
  // in the receipt opened from it. See utils/activity/displayStatus.ts.
  const display = toDisplayStatus(item.status, {
    maskFailed: maskFailedForType(item.type),
  });

  return (
    <button
      type="button"
      onClick={() => onSelect(item.raw)}
      className="w-full flex justify-between py-5 border-gray-100 dark:border-gray-800 dark:bg-[#1C2227] bg-white px-4 border-b items-center cursor-pointer text-left last:border-b-0"
    >
      <div className="flex justify-start items-center gap-2 min-w-0">
        <ActivityItemIcon item={item} giftCardImage={giftCardImage} />
        <div className="min-w-0">
          <h4 className="text-[13px] font-semibold text-[#042741] dark:text-gray-200 truncate">
            {item.title}
          </h4>
          <div className="flex items-center gap-1 mt-[2px]">
            <span className="text-[11px] text-gray-500 dark:text-gray-400">
              {formatDateWithSuffix(item.createdAt)}
            </span>
            <span className="text-[11px] text-gray-400">·</span>
            <span
              className={`text-[10px] font-bold capitalize ${
                STATUS_COLOR[display] ?? ""
              }`}
            >
              {display}
            </span>
          </div>
        </div>
      </div>
      <div className="text-right shrink-0 pl-2">
        <h4 className="text-[13px] font-semibold text-[#042741] dark:text-gray-200">
          {hideAmounts ? "*****" : item.amountPrimary}
        </h4>
        {item.amountSecondary && (
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            {hideAmounts ? "*****" : item.amountSecondary}
          </span>
        )}
      </div>
    </button>
  );
};

export default React.memo(ActivityRow);
